import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, ProgressBar } from 'react-bootstrap';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { formatDay, todayDR } from '../utils/dates';
import Loading from './Loading';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

// What the backend's stateOf (services/clientDeals.js) means, for people.
const STATE = {
  active: { label: 'Activo', bg: 'success' },
  nearly_used: { label: 'Por agotarse', bg: 'warning' },
  upcoming: { label: 'Por empezar', bg: 'info' },
  used_up: { label: 'Agotado', bg: 'secondary' },
  expired: { label: 'Vencido', bg: 'secondary' },
  cancelled: { label: 'Cancelado', bg: 'dark' },
};
const barVariant = (d) => (d.state === 'used_up' ? 'secondary' : d.used >= d.amount * 0.8 ? 'warning' : 'success');

// Conduces → "Acuerdos" (admins; backend routes/clientDeals.js): what the company owes a client in product. The client's
// conduces from the start day are deducted at their value (client's price, without ITBIS) until the balance is used up;
// then they need a factura again and show in "Sin facturar". Alerts at 80 %, at zero and 15 days before the end date.
const ClientDealsPanel = () => {
  const [deals, setDeals] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // {} = new, a deal = edit
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => API.get('/client-deals').then(r => setDeals(r.data)).catch(() => setError('No se pudieron cargar los acuerdos.')), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { API.get('/clients').then(r => setClients(r.data)).catch(() => {}); }, []);

  const open = deals ? deals.filter(d => !['used_up', 'expired', 'cancelled'].includes(d.state)) : [];
  const closed = deals ? deals.filter(d => ['used_up', 'expired', 'cancelled'].includes(d.state)) : [];

  const rows = (list) => list.map(d => (
    <tr key={d._id} style={{ cursor: 'pointer' }} onClick={() => setViewing(d._id)}>
      <td><strong>{d.client?.name || '—'}</strong><div className="small text-muted">{d.title}</div></td>
      <td className="small">{formatDay(d.startDate)}{d.endDate ? ` – ${formatDay(d.endDate)}` : ' – sin fin'}</td>
      <td className="text-end">{money(d.amount)}</td>
      <td style={{ minWidth: 180 }}>
        <ProgressBar now={Math.min(100, (d.used / d.amount) * 100)} variant={barVariant(d)} style={{ height: 8 }} />
        <div className="small text-muted mt-1">{money(d.used)} usado · {d.conduces} conduce{d.conduces === 1 ? '' : 's'}</div>
      </td>
      <td className="text-end"><strong>{money(d.balance)}</strong></td>
      <td><Badge bg={STATE[d.state]?.bg}>{STATE[d.state]?.label}</Badge></td>
    </tr>
  ));

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div className="text-muted small" style={{ maxWidth: 760 }}>
          Lo que le debemos a un cliente en producto. Sus conduces desde la fecha de inicio se descuentan del saldo a su precio
          (sin ITBIS) y no se facturan; cuando el saldo llega a cero vuelven a facturarse.
        </div>
        <Button onClick={() => setEditing({})}>+ Nuevo acuerdo</Button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <Card className="shadow-sm p-3 mb-3">
        {deals === null ? <Loading label="Cargando acuerdos..." /> : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead><tr><th>Cliente / acuerdo</th><th>Vigencia</th><th className="text-end">Monto</th><th>Usado</th><th className="text-end">Saldo</th><th>Estado</th></tr></thead>
              <tbody>
                {rows(open)}
                {open.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">No hay acuerdos activos.</td></tr>}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
      {closed.length > 0 && (
        <Card className="shadow-sm p-3">
          <Card.Title className="small text-muted text-uppercase">Agotados, vencidos y cancelados</Card.Title>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle"><tbody>{rows(closed)}</tbody></Table>
          </div>
        </Card>
      )}
      {editing && <DealForm deal={editing} clients={clients} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && (
        <DealDetail id={viewing} onClose={() => setViewing(null)} onEdit={(d) => { setViewing(null); setEditing(d); }} onChanged={load} />
      )}
    </>
  );
};

function DealForm({ deal, clients, onClose, onSaved }) {
  const isNew = !deal._id;
  const [form, setForm] = useState({
    client: deal.client?._id || '', title: deal.title || '', amount: deal.amount ?? '', startDate: deal.startDate || todayDR(),
    endDate: deal.endDate || '', notes: deal.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true); setError('');
    const body = { title: form.title, amount: Number(form.amount), startDate: form.startDate, endDate: form.endDate || null, notes: form.notes };
    try {
      if (isNew) await API.post('/client-deals', { ...body, client: form.client });
      else await API.put(`/client-deals/${deal._id}`, body);
      onSaved();
    } catch (err) { setError(err.response?.data?.message || 'No se pudo guardar el acuerdo.'); }
    finally { setSaving(false); }
  };
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>{isNew ? 'Nuevo acuerdo' : 'Editar acuerdo'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form.Group className="mb-3">
          <Form.Label>Cliente</Form.Label>
          {isNew ? (
            <SearchSelect options={clients} value={form.client} getKey={c => c._id} getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
              onSelect={c => setForm(f => ({ ...f, client: c._id }))} placeholder="Buscar cliente..." emptyLabel="Sin clientes coincidentes" />
          ) : <div><strong>{deal.client?.name}</strong></div>}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Acuerdo</Form.Label>
          <Form.Control maxLength={120} value={form.title} onChange={set('title')} placeholder="Ej.: Canje por servicios de mantenimiento 2026" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Monto en producto (RD$, sin ITBIS)</Form.Label>
          <Form.Control type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} />
        </Form.Group>
        <Row>
          <Col><Form.Group className="mb-3">
            <Form.Label>Desde</Form.Label>
            <Form.Control type="date" value={form.startDate} onChange={set('startDate')} />
            <Form.Text className="text-muted">Cuentan los conduces desde este día, también los ya hechos.</Form.Text>
          </Form.Group></Col>
          <Col><Form.Group className="mb-3">
            <Form.Label>Hasta <span className="text-muted">(opcional)</span></Form.Label>
            <Form.Control type="date" value={form.endDate} onChange={set('endDate')} />
          </Form.Group></Col>
        </Row>
        <Form.Group>
          <Form.Label>Notas <span className="text-muted">(opcional)</span></Form.Label>
          <Form.Control as="textarea" rows={2} maxLength={1000} value={form.notes} onChange={set('notes')} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving || (isNew && !form.client) || !form.title.trim() || !(Number(form.amount) > 0)}>{saving ? 'Guardando...' : 'Guardar'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function DealDetail({ id, onClose, onEdit, onChanged }) {
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const load = useCallback(() => API.get(`/client-deals/${id}`).then(r => setDeal(r.data)).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el acuerdo.')), [id]);
  useEffect(() => { load(); }, [load]);
  const cancel = async () => {
    setError('');
    try { await API.post(`/client-deals/${id}/cancel`, { reason }); setCancelling(false); await load(); onChanged(); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo cancelar el acuerdo.'); }
  };
  const unpriced = deal ? deal.entries.filter(e => e.unpriced.length) : [];
  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{deal ? `${deal.client?.name} — ${deal.title}` : 'Acuerdo'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        {!deal ? <Loading small label="Cargando..." /> : (
          <>
            <Row className="mb-3 g-2">
              <Col xs={6} md={3}><div className="small text-muted">Monto</div><strong>{money(deal.amount)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Usado</div><strong>{money(deal.used)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Saldo</div><strong style={{ color: 'var(--mm-cyan)' }}>{money(deal.balance)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Estado</div><Badge bg={STATE[deal.state]?.bg}>{STATE[deal.state]?.label}</Badge></Col>
            </Row>
            <ProgressBar now={Math.min(100, (deal.used / deal.amount) * 100)} variant={barVariant(deal)} className="mb-2" style={{ height: 8 }} />
            <div className="small text-muted mb-3">
              Vigencia: {formatDay(deal.startDate)} – {deal.endDate ? formatDay(deal.endDate) : 'sin fecha de fin'} · creado por {deal.createdBy || '—'}
              {deal.notes && <div className="mt-1">{deal.notes}</div>}
              {deal.cancelledAt && <div className="mt-1 text-danger">Cancelado por {deal.cancelledBy} el {formatDay(deal.cancelledAt)}: {deal.cancelReason}</div>}
            </div>
            {unpriced.length > 0 && (
              <div className="alert alert-warning small py-2">
                {unpriced.length} conduce{unpriced.length === 1 ? '' : 's'} con líneas sin precio, contadas en cero: {unpriced.map(e => `${e.number} (${e.unpriced.join(', ')})`).join('; ')}.
              </div>
            )}
            <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table size="sm" className="mb-0 align-middle">
                <thead><tr><th>Conduce</th><th>Fecha</th><th className="text-end">Valor</th><th className="text-end">Descontado</th><th className="text-end">Saldo</th></tr></thead>
                <tbody>
                  {deal.entries.map(e => (
                    <tr key={String(e.conduce)}>
                      <td><a href={`/conduces/${e.conduce}/print`} target="_blank" rel="noreferrer">{e.number}</a>{e.status === 'pending' && <Badge bg="warning" className="ms-2">Por entregar</Badge>}</td>
                      <td className="small">{formatDay(e.day)}</td>
                      <td className="text-end small">{money(e.value)}</td>
                      <td className="text-end">{money(e.covered)}{e.covered < e.value - 0.005 && <div className="small text-muted">resto por facturar</div>}</td>
                      <td className="text-end">{money(e.balanceAfter)}</td>
                    </tr>
                  ))}
                  {deal.entries.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">Todavía no hay conduces descontados.</td></tr>}
                </tbody>
              </Table>
            </div>
            {cancelling && (
              <Form.Group className="mt-3">
                <Form.Label>¿Por qué se cancela? Sus conduces volverán a necesitar factura.</Form.Label>
                <Form.Control maxLength={300} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {deal && !deal.cancelledAt && !cancelling && (
          <>
            <Button variant="outline-danger" className="me-auto" onClick={() => setCancelling(true)}>Cancelar acuerdo</Button>
            <Button variant="outline-secondary" onClick={() => onEdit(deal)}>Editar</Button>
          </>
        )}
        {cancelling && (
          <>
            <Button variant="outline-secondary" onClick={() => setCancelling(false)}>Volver</Button>
            <Button variant="danger" disabled={!reason.trim()} onClick={cancel}>Cancelar el acuerdo</Button>
          </>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ClientDealsPanel;
